import { NotImplementedError } from "../errors";
import { Base } from "../handlers/base";
import { FILTER_ALL } from "../handlers/filter";
import { FindPromiseBuilder } from "./builder";
import { isCacheableFind } from "./isCacheableFind";
/**
 * Promise-based handler for straightforward API interactions.
 * This class provides a common api surface for all feature modules that interact with madhive endpoints.
 *
 * ### Extending PromiseHandler
 *
 * #### Initialize Static Properties
 *
 * ```typescript
 * import type { Thing } from "../models";
 * import { validators } from "./validators";
 *
 * class Things extends PromiseHandler<
 *   Thing, // base item type
 *   Thing, // item type
 *   ReturnType<typeof validators>, // validators obj
 *   Thing | Thing[] // cached type
 * > {
 *   // Api handler modules should only have one main endpoint
 *   // if a module needs to interact with multiple endpoints
 *   // it is a sign the module should have submodules that extend PromiseHandler — for each different endpoint
 *   readonly url: string;
 *
 *   constructor(sdk: MadSDK) {
 *    // Required:
 *    // call super with sdk, unique module name, and cache config
 *     super(
 *       sdk,
 *       // best practice is camelCase of the class name.
 *       // if the class is a submodule, use dot notation
 *       // this is used in cache keys and logging
 *       "things",
 *      // cache config
 *       { atomize: true }
 *     );
 *     this.validators = validators(sdk);
 *     this.url = `${this.sdk.urls.baseAPIUrl}/things`;
 *   }
 * }
 * ```
 *
 * #### Implement Abstract Methods
 * - {@link findItems} — used by {@link find} and {@link find_once}
 * - {@link saveItem} — used by {@link save}
 * - {@link deleteItem} — used by {@link delete}
 *
 *
 * #### Throw `NotImplementedError`
 * If your module does not support a method, throw NotImplementedError
 * to inform users that the method is intentionally unimplemented.
 * For example, if your module does not support deletion:
 * ```ts
 * protected async deleteItem(id: string): Promise<Thing> {
 *   throw new NotImplementedError("Delete not supported for Things");
 * }
 * ```
 *
 * ### Basic Usage
 * assuming `sdk` is an instance of `MadSDK` and `things` has been properly initialized with `Things` class from examples below.
 * #### fetching things
 * implement {@link findItems} — used by {@link find} and {@link find_once}
 * ```ts
 * const listOfThings = await sdk.things.find({
 *    where: [{ field: "name", type: FilterTypes.EQ, value: "example" }]
 * }, "name asc");
 *
 * const singleThing = await sdk.things.find_once({
 *   where: [{ field: "id", type: FilterTypes.EQ, value: "thing-id" }]
 * });
 * ```
 * #### Saving Things
 * implement {@link saveItem} — used by {@link save}
 * ```ts
 * const newThing = await sdk.things.save({ name: "New Thing" });
 * const updatedThing = await sdk.things.save({ id: "thing-id", name: "Updated Name" });
 * ```
 *
 * #### Deleting Things
 * implement {@link deleteItem} — used by {@link delete}
 * ```ts
 * await sdk.things.delete("thing-id");
 * ```
 *
 * @category API Handlers
 * @template BaseType The base type of the data being handled.
 * @template UniqueType The unique type of the data being handled.
 * @template Validators The type of validators to apply, which can be a set of validation methods or never if no validation is needed.
 * @template CacheType The type of cache used for storing results, defaulting to a Promise of an array of UniqueType.
 * @template FilterType The type of filter to apply when searching for items, defaulting to UniqueType.
 * @template SortableFields The fields that can be used for sorting the results, defaulting to the keys of UniqueType.
 * @template SaveType The type of data returned after saving, defaulting to UniqueType.
 * @template SaveOptions The options to use when saving data, defaulting to DefaultSaveOptions.
 * @template HydrateType The type of fields to hydrate, defaulting to string.
 */
export class PromiseHandler extends Base {
    constructor() {
        super(...arguments);
        /**
         * Find is used to search a collection using given filters, sort, and
         * hydration and will return an array of that collection type.
         * this calls {@link findItems} which must be implemented by the extending class.
         *
         * @param filters Filters to be used on the find.
         * @param sort Sort to be used on the find.
         * @param hydratedFields Fields to be hydrated on the find.
         */
        this.find = (filters, sort, hydratedFields) => {
            return new FindPromiseBuilder({
                identifierKey: this.getIdentifierKey(),
                filters,
                sort,
                hydratedFields,
                execute: this.executeFind.bind(this)
            });
        };
        /**
         * find_once is used to search a collection and only return the
         * top most record from the result set.
         *
         * this just calls {@link findItems} and returns the first result
         * in the returned data. Some collections will override this default
         * action to call a better look up.
         * @param filters Filters to be used on the find
         */
        this.find_once = (filters, hydratedFields) => {
            return new FindPromiseBuilder({
                identifierKey: this.getIdentifierKey(),
                filters,
                hydratedFields,
                execute: this.executeFindOnce.bind(this)
            });
        };
        /**
         * Save will take data in form of the collections datatype.
         * Usually the collection will check an "id" field. If there
         * is and id they it will update the record otherwise it
         * will create a new record.
         *
         * @param data data to be saved.
         */
        this.save = async (data, options) => {
            return this.saveItem(data, options).then((saved) => {
                this.cache.reconcile("save", saved);
                return saved;
            });
        };
        /**
         * Delete will take an ID and delete the associated
         * data object. Delete can just be a marker also
         * doesn't need to remove the data.
         *
         * @param id Id of the data to be deleted.
         */
        this.delete = async (id) => {
            return this.deleteItem(id).then((result) => {
                this.cache.reconcile("delete", result);
                return result;
            });
        };
        this.keyFromFilter = (filters) => {
            if (filters.where) {
                return filters.where.reduce((acc, condition) => {
                    return `${acc}-${String(condition.field)}-${condition.type}-${condition.value}`;
                }, "");
            }
            return FILTER_ALL;
        };
    }
    executeFind(config) {
        const { filters, hydratedFields, sort } = config;
        /**
         * Don't pluck from cache if user has requested hydrated fields.
         * Hydrated requests are not cached.
         */
        if (isCacheableFind(config)) {
            const plucked = this.cache.pluck(filters);
            if (plucked) {
                return Promise.resolve(plucked);
            }
        }
        return this.findItems(filters || {}, sort, hydratedFields);
    }
    executeFindOnce(config) {
        const { filters, hydratedFields } = config;
        return this.find(filters, undefined, hydratedFields).then((results) => results.length > 0 ? results[0] : null);
    }
    /**
     * Make will return a generated version default version of the Model
     * @param defaults Default data to initialize the model with
     * @return Model with defaults filled in
     */
    make(defaults) {
        throw new NotImplementedError(`Make ${defaults ? `using ${defaults}` : ""}`);
    }
}
//# sourceMappingURL=promise.js.map